import React, { useState } from 'react';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import api from '../../api';
import PurchaseDetailsModal from '../purchase/PurchaseDetailsModal';
import { formatNumber, toExportNumber } from '../../utils/formatters';
import { DEFAULT_VAT_RATE } from '../../utils/vatRate';

const QUARTERS = [
  { value: 1, label: 'Q1 (Jan - Mar)' },
  { value: 2, label: 'Q2 (Apr - Jun)' },
  { value: 3, label: 'Q3 (Jul - Sep)' },
  { value: 4, label: 'Q4 (Oct - Dec)' }
];

const keyOf = (item) => `${item.isExpense ? 'expense' : 'purchase'}-${item.purchaseId}`;

const VATSelectionReport = () => {
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-01'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [companyFilter, setCompanyFilter] = useState('');
  const [descriptionFilter, setDescriptionFilter] = useState('all');
  const [vatFilter, setVatFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [invoiceSearch, setInvoiceSearch] = useState('');

  const [selectedPurchaseId, setSelectedPurchaseId] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [scheduleYear, setScheduleYear] = useState('');
  const [scheduleQuarter, setScheduleQuarter] = useState('');
  const [realizing, setRealizing] = useState(false);

  const fetchVATReport = async () => {
    setLoading(true);
    setSelectedKeys(new Set());
    try {
      const [response, realizedKeysResponse] = await Promise.all([
        api.get(`/reports/purchasing?startDate=${startDate}&endDate=${endDate}&companyId=${companyFilter}&vatOnly=true`),
        api.get('/reports/vat-schedule-02/keys')
      ]);
      const reportRows = Array.isArray(response.data) ? response.data : [];
      const realizedKeys = new Set(Array.isArray(realizedKeysResponse.data) ? realizedKeysResponse.data : []);

      const detailedPurchases = await Promise.all(
        reportRows
          .filter((row) => row.source !== 'expense')
          .map(async (purchase) => {
            try {
              const detailResponse = await api.get(`/purchases/${purchase.id}`);
              return {
                ...detailResponse.data,
                source: 'purchase',
                description: 'Purchases',
                purchaseId: detailResponse.data.id
              };
            } catch (error) {
              console.error(`Error fetching details for purchase ${purchase.id}:`, error);
              return {
                ...purchase,
                items: [],
                source: 'purchase',
                description: 'Purchases',
                purchaseId: purchase.id
              };
            }
          })
      );

      const expenseRows = reportRows
        .filter((row) => row.source === 'expense')
        .map((expense) => ({
          ...expense,
          id: expense.id,
          purchaseId: expense.id,
          invoiceDate: expense.purchase_date,
          invoiceNo: expense.invoice_no,
          supplierTIN: expense.supplier_vat_number || '—',
          supplierName: expense.supplier_name,
          netAmount: parseFloat(expense.net_amount || 0),
          vatAmount: parseFloat(expense.vat_amount || 0),
          disallowableVATAmount: 0,
          description: 'Expense',
          isExpense: true
        }));

      setReportData({ rows: [...detailedPurchases, ...expenseRows], realizedKeys });
    } catch (error) {
      console.error('Error fetching VAT Selection report:', error);
      alert('Failed to fetch VAT Selection report');
      setReportData({ rows: [], realizedKeys: new Set() });
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (purchaseId) => {
    setSelectedPurchaseId(purchaseId);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPurchaseId(null);
  };

  const processVATData = () => {
    const rows = reportData.rows || [];
    const realizedKeys = reportData.realizedKeys || new Set();
    const vatData = [];
    const invoiceMap = new Map();

    rows.forEach((entry) => {
      const invoiceKey = entry.source === 'expense' ? `expense-${entry.id}` : entry.id;
      if (invoiceMap.has(invoiceKey)) {
        return;
      }

      let totalNetAmount = 0;
      let totalVATAmount = 0;
      let totalDisallowableVAT = 0;
      let invoiceDate = entry.invoice_date || entry.purchase_date || entry.invoiceDate || entry.bill_date;
      let invoiceNo = entry.invoice_no || entry.bill_number || entry.invoiceNo || 'N/A';
      let supplierTIN = entry.supplier_vat_number || entry.supplierTIN || '—';
      let supplierName = entry.supplier_name || entry.supplierName || 'Expense';
      let description = entry.description || (entry.source === 'expense' ? 'Expense' : 'Purchases');
      let purchaseId = entry.purchaseId || entry.id;

      if (entry.source === 'expense') {
        totalNetAmount = parseFloat(entry.netAmount || entry.net_amount || 0);
        totalVATAmount = parseFloat(entry.vatAmount || entry.vat_amount || 0);
        totalDisallowableVAT = 0;
      } else if (entry.items && entry.items.length > 0) {
        const entryVatRate = parseFloat(entry.vat_rate) || DEFAULT_VAT_RATE;
        entry.items.forEach((item) => {
          const amount = parseFloat(item.quantity || 0) * parseFloat(item.unit_price || 0);
          const vatAmount = amount * (entryVatRate / 100);
          const vatApplicable = item.vat_applicable !== undefined ? item.vat_applicable : true;

          totalNetAmount += amount;

          if (vatApplicable) {
            totalVATAmount += vatAmount;
          } else {
            totalDisallowableVAT += vatAmount;
          }
        });
      } else {
        const netAmount = parseFloat(entry.bill_amount || 0) - parseFloat(entry.vat_amount || 0);
        const vatAmount = parseFloat(entry.vat_amount || 0);
        totalNetAmount = netAmount;
        totalVATAmount = vatAmount;
        totalDisallowableVAT = 0;
      }

      const invoiceData = {
        purchaseId,
        invoiceDate,
        invoiceNo,
        supplierTIN,
        supplierName,
        description,
        netAmount: totalNetAmount,
        vatAmount: totalVATAmount,
        disallowableVATAmount: totalDisallowableVAT,
        isExpense: entry.source === 'expense',
        companyId: entry.company_id || null
      };

      invoiceMap.set(invoiceKey, invoiceData);

      // Already realized onto Scheduled 02 - not selectable again.
      if (!realizedKeys.has(keyOf(invoiceData))) {
        vatData.push(invoiceData);
      }
    });

    let filteredData = vatData;

    if (descriptionFilter !== 'all') {
      filteredData = filteredData.filter((item) => item.description === descriptionFilter);
    }

    if (vatFilter === 'yes') {
      filteredData = filteredData.filter((item) => item.vatAmount > 0);
    }

    if (vatFilter === 'no') {
      filteredData = filteredData.filter((item) => item.vatAmount === 0);
    }

    const supplierOptions = Array.from(new Set(filteredData.map((item) => item.supplierName).filter(Boolean))).sort();

    if (supplierFilter) {
      filteredData = filteredData.filter((item) => item.supplierName === supplierFilter);
    }

    if (invoiceSearch.trim()) {
      const needle = invoiceSearch.trim().toLowerCase();
      filteredData = filteredData.filter((item) => String(item.invoiceNo || '').toLowerCase().includes(needle));
    }

    return { data: filteredData, supplierOptions };
  };

  const { data: vatData, supplierOptions } = processVATData();
  const totalNetAmount = vatData.reduce((sum, item) => sum + item.netAmount, 0);
  const totalVATAmount = vatData.reduce((sum, item) => sum + item.vatAmount, 0);
  const totalDisallowableVAT = vatData.reduce((sum, item) => sum + item.disallowableVATAmount, 0);

  const downloadExcel = () => {
    if (vatData.length === 0) {
      alert('No data to export. Please generate the report first.');
      return;
    }

    const worksheetData = [
      ['Invoice Date', 'Invoice No', 'Supplier TIN Number', 'Name of Supplier', 'Description',
        'Net Amount (Rs.)', 'VAT Amount (Rs.)', 'Disallowable VAT Amount (Rs.)']
    ];

    vatData.forEach((item) => {
      worksheetData.push([
        item.invoiceDate ? format(new Date(item.invoiceDate), 'MM/dd/yyyy') : 'N/A',
        item.invoiceNo,
        item.supplierTIN,
        item.supplierName,
        item.description,
        toExportNumber(item.netAmount, 2),
        toExportNumber(item.vatAmount, 2),
        toExportNumber(item.disallowableVATAmount, 2)
      ]);
    });

    worksheetData.push([]);
    worksheetData.push([
      'TOTAL', '', '', '', '',
      toExportNumber(totalNetAmount, 2),
      toExportNumber(totalVATAmount, 2),
      toExportNumber(totalDisallowableVAT, 2)
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    worksheet['!cols'] = [
      { wch: 16 }, { wch: 18 }, { wch: 22 }, { wch: 28 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 24 }
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'VAT Selection Report');
    XLSX.writeFile(workbook, `vat-selection-report-${startDate}-to-${endDate}.xlsx`);
  };

  const allSelected = vatData.length > 0 && vatData.every((item) => selectedKeys.has(keyOf(item)));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedKeys(new Set());
    } else {
      setSelectedKeys(new Set(vatData.map(keyOf)));
    }
  };

  const toggleSelectOne = (item) => {
    const key = keyOf(item);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleRealize = async () => {
    if (!scheduleYear || !scheduleQuarter) {
      alert('Select Year and Quarter before realizing.');
      return;
    }

    if (selectedKeys.size === 0) {
      alert('Select at least one invoice first.');
      return;
    }

    const itemsToRealize = vatData.filter((item) => selectedKeys.has(keyOf(item)));

    setRealizing(true);
    try {
      const response = await api.post('/reports/vat-schedule-02/realize', {
        items: itemsToRealize,
        year: scheduleYear,
        quarter: scheduleQuarter
      });
      alert(`${response.data.realized} invoice(s) moved to Scheduled 02.`);
      await fetchVATReport();
    } catch (error) {
      console.error('Error realizing VAT selection:', error);
      alert(error.response?.data?.error || 'Failed to realize the selected invoices');
    } finally {
      setRealizing(false);
    }
  };

  return (
    <div>
      <div className="filters-section card" style={{ display: 'flex', gap: 'var(--spacing-lg)', padding: 'var(--spacing-md)', marginBottom: 'var(--spacing-lg)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div className="report-filter-group">
          <label className="report-filter-label">Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            max={endDate}
            className="report-filter-input"
          />
        </div>

        <div className="report-filter-group">
          <label className="report-filter-label">End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            min={startDate}
            max={format(new Date(), 'yyyy-MM-dd')}
            className="report-filter-input"
          />
        </div>

        <div className="report-filter-group">
          <label className="report-filter-label">Company</label>
          <select
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
            className="report-filter-select"
          >
            <option value="">All Companies</option>
            <option value="1">JH</option>
            <option value="2">RR</option>
            <option value="3">RD</option>
          </select>
        </div>

        <div className="report-filter-group">
          <label className="report-filter-label">Description</label>
          <select
            value={descriptionFilter}
            onChange={(e) => setDescriptionFilter(e.target.value)}
            className="report-filter-select"
          >
            <option value="all">All</option>
            <option value="Purchases">Purchases</option>
            <option value="Expense">Expense</option>
          </select>
        </div>

        <div className="report-filter-group">
          <label className="report-filter-label">VAT</label>
          <select
            value={vatFilter}
            onChange={(e) => setVatFilter(e.target.value)}
            className="report-filter-select"
          >
            <option value="all">All</option>
            <option value="yes">Has VAT</option>
            <option value="no">No VAT</option>
          </select>
        </div>

        <div className="report-filter-group">
          <label className="report-filter-label">Supplier</label>
          <select
            value={supplierFilter}
            onChange={(e) => setSupplierFilter(e.target.value)}
            className="report-filter-select"
          >
            <option value="">All Suppliers</option>
            {supplierOptions.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </div>

        <div className="report-filter-group">
          <label className="report-filter-label">Invoice No</label>
          <input
            type="text"
            value={invoiceSearch}
            onChange={(e) => setInvoiceSearch(e.target.value)}
            placeholder="Search invoice no..."
            className="report-filter-input"
          />
        </div>

        <div className="report-filter-actions">
          <button
            onClick={fetchVATReport}
            disabled={loading}
            className="btn btn-primary"
          >
            {loading ? (
              <>
                <div className="loading-spinner"></div>
                Generating...
              </>
            ) : (
              'Generate VAT Selection Report'
            )}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-state" role="status" aria-live="polite">
          <div className="loading-spinner"></div>
          Loading VAT Selection report data...
        </div>
      ) : vatData.length > 0 ? (
        <>
          <div className="card vat-selection-toolbar" style={{ display: 'flex', gap: 'var(--spacing-lg)', padding: 'var(--spacing-md)', marginBottom: 'var(--spacing-lg)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="report-filter-group">
              <label className="report-filter-label">Year *</label>
              <input
                type="number"
                value={scheduleYear}
                onChange={(e) => setScheduleYear(e.target.value)}
                placeholder="YYYY"
                className="report-filter-input"
                style={{ width: '110px' }}
              />
            </div>
            <div className="report-filter-group">
              <label className="report-filter-label">Quarter *</label>
              <select
                value={scheduleQuarter}
                onChange={(e) => setScheduleQuarter(e.target.value ? parseInt(e.target.value, 10) : '')}
                className="report-filter-select"
              >
                <option value="">Select Quarter</option>
                {QUARTERS.map((q) => (
                  <option key={q.value} value={q.value}>{q.label}</option>
                ))}
              </select>
            </div>
            <div className="report-filter-actions" style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleRealize}
                disabled={realizing || selectedKeys.size === 0 || !scheduleYear || !scheduleQuarter}
                title={!scheduleYear || !scheduleQuarter ? 'Select Year and Quarter first' : undefined}
                className="btn btn-success"
              >
                {realizing ? 'Realizing...' : `Realized (${selectedKeys.size})`}
              </button>
              <button onClick={downloadExcel} className="btn btn-success">
                <span>📊</span> Export to Excel
              </button>
            </div>
          </div>

          <div className="card" style={{ padding: 'var(--spacing-lg)' }}>
            <div style={{ overflowX: 'auto' }}>
            <table className="report-table">
              <colgroup>
                <col style={{ width: '40px' }} />
                <col style={{ width: '130px' }} />
                <col style={{ width: '140px' }} />
                <col style={{ width: '180px' }} />
                <col style={{ width: '200px' }} />
                <col style={{ width: '150px' }} />
                <col style={{ width: '180px' }} />
                <col style={{ width: '180px' }} />
                <col style={{ width: '220px' }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="text-center">
                    <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                  </th>
                  <th className="text-left">Invoice Date</th>
                  <th className="text-left">Invoice No</th>
                  <th className="text-left">Supplier TIN Number</th>
                  <th className="text-left">Name of Supplier</th>
                  <th className="text-left">Description</th>
                  <th className="text-right">Net Amount (Rs.)</th>
                  <th className="text-right">VAT Amount (Rs.)</th>
                  <th className="text-right">Disallowable VAT Amount (Rs.)</th>
                </tr>
              </thead>
              <tbody>
                {vatData.map((item, index) => (
                  <tr key={`${item.invoiceNo}-${index}`}>
                    <td className="text-center">
                      <input
                        type="checkbox"
                        checked={selectedKeys.has(keyOf(item))}
                        onChange={() => toggleSelectOne(item)}
                      />
                    </td>
                    <td
                      className="text-left clickable-row"
                      onClick={item.isExpense ? undefined : () => handleRowClick(item.purchaseId)}
                      title={item.isExpense ? '' : 'Click to view full purchase details'}
                    >
                      {item.invoiceDate ? format(new Date(item.invoiceDate), 'dd/MM/yyyy') : 'N/A'}
                    </td>
                    <td className="text-left">{item.invoiceNo}</td>
                    <td className="text-left">{item.supplierTIN}</td>
                    <td className="text-left">{item.supplierName}</td>
                    <td className="text-left">{item.description}</td>
                    <td className="report-table-value-numeric">{formatNumber(item.netAmount, 2, 2)}</td>
                    <td className="report-table-value-numeric text-primary">{formatNumber(item.vatAmount, 2, 2)}</td>
                    <td className="report-table-value-numeric text-danger">{formatNumber(item.disallowableVATAmount, 2, 2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="6" className="text-right"><strong>TOTAL</strong></td>
                  <td className="report-table-value-numeric"><strong>{formatNumber(totalNetAmount, 2, 2)}</strong></td>
                  <td className="report-table-value-numeric text-primary"><strong>{formatNumber(totalVATAmount, 2, 2)}</strong></td>
                  <td className="report-table-value-numeric text-danger"><strong>{formatNumber(totalDisallowableVAT, 2, 2)}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
          </div>
        </>
      ) : (
        <div className="card" style={{ textAlign: 'center', padding: 'var(--spacing-3xl)', color: 'var(--color-text-secondary)' }}>
          <div style={{ fontSize: '48px', marginBottom: 'var(--spacing-md)', opacity: 0.5 }}>📊</div>
          <h3>No Data Available</h3>
          <p>No data to display. Select a date range and generate the report.</p>
        </div>
      )}

      {showModal && selectedPurchaseId && (
        <PurchaseDetailsModal
          purchaseId={selectedPurchaseId}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default VATSelectionReport;
